import translate from '@allesauto/i18n';
import { Background } from '@allesauto/utils/consts';
import { Routes_DE as Routes } from '@allesauto/utils/routes';
import { ArrowRight32 } from '@carbon/icons-react';
import clsx from 'clsx';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';

import './ServiceTile.css';

import NextLink from '../nextLink';

export interface ServiceTileProps {
  linkTo?: string;
  background?: Background;
  name: string;
  description: string;
  ctaLabel?: string;
  isCarouselMoving?: boolean;
  id: string;
  abstractProductSku: string;
  onClickHandler?: (
    id: string,
    description: string,
    title: string,
    abstractProductSku: string
  ) => void;
}

const ServiceTile: FC<ServiceTileProps> = ({
  linkTo = Routes.SERVICE_BOOKING,
  background = Background.WHITE,
  name,
  description,
  ctaLabel = 'ui.SERVICE_BOOKING.BOOK_SERVICE',
  isCarouselMoving,
  onClickHandler = () => null,
  id,
  abstractProductSku,
}: ServiceTileProps) => {
  const intl = useIntl();
  const trackingIdentifierTile = `aa-garage--service--tile`;

  const className = {
    linkStyles: clsx(
      'flex flex-col group h-full no-underline overflow-hidden service-tile w-full',
      'p-40px pb-30px relative tile-href-grid',
      background === Background.WHITE && 'bg-white',
      background === Background.GREY && 'bg-grey10',
      'after:wl-bg-highlight'
    ),
    wrapperStyles:
      'flex flex-1 flex-col max-h-[340px] overflow-hidden text-ellipsis',
    headlineStyles: clsx(
      'max-h-[72px] mb-20px text-headline4 text-purple80 monochrome:text-black',
      'capitalize hyphens-auto overflow-hidden service-tile-headline text-ellipsis'
    ),
    descriptionStyles: clsx(
      'max-h-[248px] service-tile-description text-grey80 text-p1',
      '!leading-6 overflow-hidden text-ellipsis'
    ),
    footerStyles: 'self-end mt-20px flex w-full justify-between items-center',
    ctaLabelStyles:
      'text-purple80 monochrome:text-black text-p1 opacity-100 lg:opacity-0 lg:group-hover:opacity-100',
    ctaIconStyles: 'fill-purple80 monochrome:fill-black flex-none',
  };

  return (
    <NextLink
      href={isCarouselMoving ? '#' : linkTo}
      onClick={() => onClickHandler(id, description, name, abstractProductSku)}
      className={className.linkStyles}
      data-tracking-id={trackingIdentifierTile}
    >
      <div className={className.wrapperStyles}>
        <h4 className={className.headlineStyles}>{name}</h4>
        <p className={className.descriptionStyles}>{description}</p>
      </div>
      <div className={className.footerStyles}>
        <span className={className.ctaLabelStyles}>
          {translate(intl, ctaLabel)}
        </span>
        <ArrowRight32
          className={className.ctaIconStyles}
          width={40}
          height={40}
        />
      </div>
    </NextLink>
  );
};

export default ServiceTile;
